import { clsx } from 'clsx';
import type { CSSProperties } from 'react';

interface IconProps {
  size?: number;
  className?: string;
  filled?: boolean;
}

/**
 * Thin wrapper around Material Symbols Outlined. The stylesheet is loaded in
 * index.html. Each named export below maps to a glyph so existing call sites
 * (`<HomeIcon size={20} />`) keep working unchanged.
 */
function Symbol({
  name,
  size = 20,
  className,
  filled = false,
}: { name: string } & IconProps) {
  const style: CSSProperties = {
    fontSize: `${size}px`,
    fontVariationSettings: filled
      ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
      : "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24",
    lineHeight: 1,
  };
  return (
    <span
      className={clsx('material-symbols-outlined', className)}
      style={style}
      aria-hidden="true"
    >
      {name}
    </span>
  );
}

export function HomeIcon(p: IconProps) { return <Symbol name="grid_view" {...p} />; }
export function DownloadIcon(p: IconProps) { return <Symbol name="cloud_download" {...p} />; }
export function UploadIcon(p: IconProps) { return <Symbol name="cloud_upload" {...p} />; }
export function CreditCardIcon(p: IconProps) { return <Symbol name="payments" {...p} />; }
export function LogsIcon(p: IconProps) { return <Symbol name="receipt_long" {...p} />; }
export function SettingsIcon(p: IconProps) { return <Symbol name="settings" {...p} />; }
export function SetupIcon(p: IconProps) { return <Symbol name="rocket_launch" {...p} />; }
export function UserIcon(p: IconProps) { return <Symbol name="person" {...p} />; }
export function HelpCircleIcon(p: IconProps) { return <Symbol name="help" {...p} />; }
export function LogOutIcon(p: IconProps) { return <Symbol name="logout" {...p} />; }
export function ChevronLeftIcon(p: IconProps) { return <Symbol name="chevron_left" {...p} />; }
export function ChevronRightIcon(p: IconProps) { return <Symbol name="chevron_right" {...p} />; }
export function CheckIcon(p: IconProps) { return <Symbol name="check" {...p} />; }
export function AlertCircleIcon(p: IconProps) { return <Symbol name="error" {...p} />; }
export function WebhookIcon(p: IconProps) { return <Symbol name="webhook" {...p} />; }
export function CalendarIcon(p: IconProps) { return <Symbol name="calendar_month" {...p} />; }
export function FilterIcon(p: IconProps) { return <Symbol name="filter_list" {...p} />; }
export function MenuIcon(p: IconProps) { return <Symbol name="menu" {...p} />; }
export function CopyIcon(p: IconProps) { return <Symbol name="content_copy" {...p} />; }

// Re-export the raw Symbol helper for page-level usage where we want custom glyphs
export { Symbol as MaterialIcon };
